import {
	IsNotEmpty,
	IsOptional,
	IsString,
	IsUUID,
	Length,
	Matches,
	MaxLength,
	Validate,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from 'class-validator';

import { CoursesParsableUrlConstraint } from './courses-challenges.dto';

export class CoursesTagInviteTokenDto {
	@IsString()
	@Length(4, 128)
	token: string;
}

const COURSES_EVENT_KEY_PATTERN = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
const COURSES_PAYLOAD_KEY_PATTERN = /^[a-z][a-z0-9_]*$/;
const COURSES_EVENT_KEY_RULE =
	'Use domain.action in lowercase snake_case, e.g. lesson.resource_downloaded';
const COURSES_PAYLOAD_KEY_RULE = 'Payload fields must be lowercase snake_case, e.g. lesson_id';

export function validateCoursesEventPayload(value: unknown): string | null {
	if (value === null || typeof value !== 'object' || Array.isArray(value)) {
		return 'Payload must be an object';
	}
	for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
		if (key.length > 60) {
			return `Payload field "${key}" must be 60 characters or fewer`;
		}
		if (!COURSES_PAYLOAD_KEY_PATTERN.test(key)) {
			return COURSES_PAYLOAD_KEY_RULE;
		}
		const scalar =
			item === null ||
			typeof item === 'boolean' ||
			(typeof item === 'number' && Number.isFinite(item)) ||
			(typeof item === 'string' && item.length <= 500);
		if (!scalar) {
			return `Payload field "${key}" must be a string (max 500), number, boolean or null`;
		}
	}
	if (Object.keys(value).length > 25) {
		return 'Too many payload fields';
	}

	return null;
}

@ValidatorConstraint({ name: 'CoursesEventPayload' })
class CoursesEventPayloadConstraint implements ValidatorConstraintInterface {
	validate(value: unknown): boolean {
		return validateCoursesEventPayload(value) === null;
	}

	defaultMessage(args: ValidationArguments): string {
		return validateCoursesEventPayload(args.value) ?? 'Invalid payload';
	}
}

const ISO_UTC_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/;

export class CoursesTrackEventDto {
	@IsString()
	@MaxLength(80)
	@Matches(COURSES_EVENT_KEY_PATTERN, { message: COURSES_EVENT_KEY_RULE })
	key: string;

	@IsOptional()
	@Validate(CoursesEventPayloadConstraint)
	payload?: Record<string, string | number | boolean | null>;

	@IsOptional()
	@IsUUID()
	courseId?: string | null;

	@IsOptional()
	@IsString()
	@Matches(ISO_UTC_DATETIME_RE, { message: 'Invalid datetime' })
	occurredAt?: string;
}

/** Any URL `new URL` accepts; the scheme is checked when the preview is fetched. */
export class CoursesLinkPreviewQueryDto {
	@IsString()
	@MaxLength(2048)
	@Validate(CoursesParsableUrlConstraint)
	url: string;
}

export class CoursesProcessUnsubscribeDto {
	@IsString()
	@IsNotEmpty()
	@MaxLength(1024)
	token: string;
}
